import { useStore } from 'effector-react';
import { useEffect } from 'react';
import FilterLine from 'components/FilterLine';
import FilterResult from 'components/FilterResult';
import LoadMoreButton from 'components/LoadMoreButton';
import NoContent from 'components/NoContent';
import {
	clearPiiFilter,
	piiFilterToArray,
	piiFilterWithGroupsStore,
} from 'components/PiiGlobalFilterV2/model';
import Preloader from 'components/Preloader';
import ResetFilters from 'components/ResetFilters';
import EnhancedTableHead, { TOrder } from 'components/table/EnhancedTableHead';
import GridBody from 'components/table/GridBody';
import GridTable from 'components/table/GridTable';
import { Typo } from 'components/typography/Typo';
import { NoSQLDatabaseSampleItem, NoSQLDatabaseSchemaItem } from 'models/noSQLDatabases/dto';
import { noSQLDatabaseSamplesModel } from 'models/noSQLDatabases/model';
import { PageParamsConfig, usePageParams } from 'services/pageParams';
import commonStyles from '../../index.module.css';
import tableConfig from './config';
import styles from './index.module.css';
import { SampleRow } from './SampleRow';

const pageConfig = {
	'data-types': {
		type: 'numberArray',
		persistence: 'session',
	},
	nonempty: {
		type: 'boolean',
		persistence: 'session',
	},
	sort: {
		type: 'sort',
		persistence: 'session',
	},
} satisfies PageParamsConfig;

type Props = {
	schemaId: NoSQLDatabaseSchemaItem['id'];
};

function SamplesTable({ schemaId }: Readonly<Props>) {
	const state = useStore(noSQLDatabaseSamplesModel.store);
	const piiFilterWithGroups = useStore(piiFilterWithGroupsStore);
	const [pageParams, setPageParams] = usePageParams(pageConfig, 'NoSQLSamples');

	useEffect(() => {
		setPageParams({
			...pageParams,
			'data-types': piiFilterWithGroups.dataTypes,
			nonempty: piiFilterWithGroups.nonEmpty,
		});
	}, [piiFilterWithGroups]);

	useEffect(() => {
		const { sort } = pageParams;

		const dataTypes = piiFilterToArray(piiFilterWithGroups);
		const paramsForFx = {
			sort: { orderBy: sort.value as keyof NoSQLDatabaseSampleItem, order: sort.operator },
			'data-types': dataTypes,
		};

		noSQLDatabaseSamplesModel.fetchFx({ id: schemaId, ...paramsForFx });
	}, [pageParams]);

	// Clean up model on unmount
	useEffect(() => {
		return () => {
			noSQLDatabaseSamplesModel.resetFx();
		};
	}, []);

	function onSortUpdate(property: keyof NoSQLDatabaseSampleItem) {
		const { sort } = pageParams;

		const operator: TOrder = sort.value === property && sort.operator === 'asc' ? 'desc' : 'asc';
		const newParams = { sort: { operator, value: property } };

		setPageParams({ ...pageParams, ...newParams });
	}

	function onResetFilters() {
		const newParams = {
			'data-types': [],
		};

		setPageParams({ ...pageParams, ...newParams });
		clearPiiFilter();
	}
	const { status, data, total, total_filtered, hasMoreData } = state;

	const hasFilter = pageParams['data-types'].length > 0;

	function contentWithCondition() {
		if (data.length > 0) {
			return data.map((sample) => <SampleRow sample={sample} key={sample.id} />);
		} else if (hasFilter) {
			return <ResetFilters onReset={onResetFilters} />;
		} else {
			return (
				<NoContent type="noSQLSchemaSamples" className={styles.rowContainer}>
					<div className={commonStyles.emptyContainer}>
						<Typo variant="D/Regular/Body-S" color="secondary">
							No available analytics.
						</Typo>
					</div>
				</NoContent>
			);
		}
	}

	return (
		<div>
			<Typo variant="D/Medium/H100-Header">Data samples</Typo>

			<FilterLine
				config={['dataTypes']}
				values={{
					dataTypes: pageParams['data-types'],
				}}
				onChange={() => {}}
			/>

			<FilterResult
				entityLabel="data sample"
				loading={status === 'loading'}
				total={total}
				totalFiltered={total_filtered}
			/>

			<GridTable dataTest="samples-table">
				<EnhancedTableHead
					config={tableConfig}
					order={pageParams.sort.operator}
					orderBy={pageParams.sort.value}
					onRequestSort={onSortUpdate}
					rowClassname={styles.rowContainer}
				/>

				<Preloader isLoading={status === 'initial' || status === 'loading'}>
					<GridBody data-test="samples-list">
						{contentWithCondition()}

						<LoadMoreButton
							show={hasMoreData}
							loading={status === 'loadingMore'}
							request={noSQLDatabaseSamplesModel.fetchMoreFx}
						/>
					</GridBody>
				</Preloader>
			</GridTable>
		</div>
	);
}

export { SamplesTable };
